/**
 * Pending Subscriptions API wrapper
 *
 * JSON endpoint: /api/v1/pending-subscriptions
 * POST /api/v1/pending-subscriptions - Create new pending subscription
 * POST /api/v1/pending-subscriptions - Update pending subscription (with id)
 * GET /api/v1/pending-subscriptions?id={id} - Get pending subscription by id
 * GET /api/v1/pending-subscriptions - Get pending subscriptions with query params
 * DELETE /api/v1/pending-subscriptions?id={id} - Hard delete a pending subscription
 */

import { api } from "../common/utils"
import { PENDING_SUBSCRIPTIONS_ENDPOINT } from "../common/constants"
import {
  CreatePendingSubscriptionPayload,
  UpdatePendingSubscriptionPayload,
  PendingSubscriptionResponse,
  PendingSubscriptionsGetResponse,
  ForcePriceResponse,
  APIError
} from "../types"

// Helper function to transform flat payload to nested backend format
const transformToApiFormat = (payload: any) => {
  // If quote_info is already provided as a nested object, clean and use it
  if (payload.quote_info && typeof payload.quote_info === "object") {
    const apiPayload: any = {}

    // Copy top-level fields
    if (payload.id !== undefined) apiPayload.id = payload.id
    if (payload.email !== undefined) apiPayload.email = payload.email
    if (payload.trigger_rate !== undefined)
      apiPayload.trigger_rate = payload.trigger_rate
    if (payload.nmls !== undefined) apiPayload.nmls = payload.nmls
    if (payload.first_name !== undefined)
      apiPayload.first_name = payload.first_name
    if (payload.last_name !== undefined)
      apiPayload.last_name = payload.last_name
    if (payload.phone !== undefined) apiPayload.phone = payload.phone
    if (payload.loid !== undefined) apiPayload.loid = payload.loid
    if (payload.client_request_id !== undefined)
      apiPayload.client_request_id = payload.client_request_id
    if (payload.status !== undefined) apiPayload.status = payload.status

    // Clean quote_info by removing fields that don't belong
    const {
      active,
      deleted,
      waive_escrows,
      first_time_homebuyer,
      ...cleanQuoteInfo
    } = payload.quote_info

    apiPayload.quote_info = cleanQuoteInfo

    return apiPayload
  }

  // Otherwise, transform flat structure to nested format
  const {
    email,
    trigger_rate,
    nmls,
    first_name,
    last_name,
    phone,
    loid,
    zipcode,
    city,
    county,
    state,
    street_address,
    client_request_id,
    taxes,
    insurance,
    seller_credit,
    id,
    status,
    ...rest
  } = payload

  const apiPayload: any = {}

  // Add top-level fields if they exist
  if (id !== undefined) apiPayload.id = id
  if (email !== undefined) apiPayload.email = email
  if (trigger_rate !== undefined) apiPayload.trigger_rate = trigger_rate
  if (nmls !== undefined) apiPayload.nmls = nmls
  if (first_name !== undefined) apiPayload.first_name = first_name
  if (last_name !== undefined) apiPayload.last_name = last_name
  if (phone !== undefined) apiPayload.phone = phone
  if (loid !== undefined) apiPayload.loid = loid
  if (client_request_id !== undefined)
    apiPayload.client_request_id = client_request_id
  if (status !== undefined) apiPayload.status = status

  // Build quote_info if any quote-related fields exist
  const hasQuoteFields =
    zipcode !== undefined ||
    city !== undefined ||
    county !== undefined ||
    state !== undefined ||
    street_address !== undefined ||
    taxes !== undefined ||
    insurance !== undefined ||
    seller_credit !== undefined ||
    Object.keys(rest).length > 0

  if (hasQuoteFields) {
    apiPayload.quote_info = {
      ...rest
    }

    // Add location if any location fields exist
    const hasLocationFields =
      zipcode !== undefined ||
      city !== undefined ||
      county !== undefined ||
      state !== undefined ||
      street_address !== undefined

    if (hasLocationFields) {
      apiPayload.quote_info.location = {}
      if (city !== undefined) apiPayload.quote_info.location.city = city
      if (county !== undefined) apiPayload.quote_info.location.county = county
      if (state !== undefined) apiPayload.quote_info.location.state = state
      if (street_address !== undefined)
        apiPayload.quote_info.location.street_address = street_address
      if (zipcode !== undefined)
        apiPayload.quote_info.location.zipcode = zipcode
    }

    // Add admin_fields if any admin fields exist
    const hasAdminFields =
      taxes !== undefined ||
      insurance !== undefined ||
      seller_credit !== undefined

    if (hasAdminFields) {
      apiPayload.quote_info.admin_fields = {}
      if (taxes !== undefined) apiPayload.quote_info.admin_fields.taxes = taxes
      if (insurance !== undefined)
        apiPayload.quote_info.admin_fields.insurance = insurance
      if (seller_credit !== undefined)
        apiPayload.quote_info.admin_fields.seller_credit = seller_credit
    }
  }

  return apiPayload
}

const pendingSubscriptions = {
  create: async (
    payload: CreatePendingSubscriptionPayload
  ): Promise<PendingSubscriptionResponse | APIError> => {
    if (!payload.email || !payload.trigger_rate) {
      throw new Error("Required fields missing: email, trigger_rate")
    }
    if (!payload.nmls && !payload.loid) {
      throw new Error("Either nmls or loid is required")
    }
    if (!payload.zipcode) {
      throw new Error("zipcode is required")
    }

    const apiPayload = transformToApiFormat(payload)

    const { data } = await api.post<PendingSubscriptionResponse>(
      PENDING_SUBSCRIPTIONS_ENDPOINT,
      apiPayload
    )
    return data
  },

  update: async (
    payload: UpdatePendingSubscriptionPayload
  ): Promise<PendingSubscriptionResponse | APIError> => {
    if (!payload.id) {
      throw new Error("id is required for update")
    }

    const apiPayload = transformToApiFormat(payload)

    const { data } = await api.post<PendingSubscriptionResponse>(
      PENDING_SUBSCRIPTIONS_ENDPOINT,
      apiPayload
    )
    return data
  },

  get: async (
    id: number
  ): Promise<PendingSubscriptionsGetResponse | APIError> => {
    if (!id) {
      throw new Error("id is required")
    }

    const { data } = await api.get<PendingSubscriptionsGetResponse>(
      `${PENDING_SUBSCRIPTIONS_ENDPOINT}?id=${id}`
    )
    return data
  },

  getByParams: async (
    params: any
  ): Promise<PendingSubscriptionsGetResponse | APIError> => {
    const { data } = await api.get<PendingSubscriptionsGetResponse>(
      PENDING_SUBSCRIPTIONS_ENDPOINT,
      { params }
    )
    return data
  },

  forcePrice: async (id: number): Promise<ForcePriceResponse | APIError> => {
    if (!id) {
      throw new Error("id is required for force_price")
    }

    const { data } = await api.post<ForcePriceResponse>(
      PENDING_SUBSCRIPTIONS_ENDPOINT,
      { id, action: "force_price" }
    )
    return data
  },

  delete: async (
    id: number
  ): Promise<PendingSubscriptionResponse | APIError> => {
    if (!id) {
      throw new Error("id is required for delete")
    }

    const { data } = await api.delete<PendingSubscriptionResponse>(
      `${PENDING_SUBSCRIPTIONS_ENDPOINT}?id=${id}`
    )
    return data
  }
}

export default pendingSubscriptions
